import { Permission } from '@/models/user';
import { useAuthentication } from '@/hooks/useAuthentication';
import { useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { getPathnamesOfItems } from '@starjack/layout';
import { useSidebarItems } from '@/layouts/sidebar/config';

const comparePermissions = (
    permissionsHolding: Permission[] = [],
    permissionsRequired: Permission[] = []
) => {
    if (!permissionsRequired.length) return true;
    if (!permissionsHolding.length && permissionsRequired.length) return false;

    return permissionsRequired.some((permission) => {
        return permissionsHolding.includes(permission);
    });
};

/** Paths allowed for B2B users in addition to sidebar paths (e.g. detail sub-routes) */
const B2B_ALLOWED_EXTRA_PATHS = ['/tickets/details'];

type UserIsAuthorized = [
    isAuthorized: boolean,
    options: {
        checkPermissions: (permissionsNeeded: Permission[]) => boolean;
    },
];

export const useIsAuthorized = (
    permissionsRequired: Permission[]
): UserIsAuthorized => {
    const location = useLocation();
    const pathname = location.pathname;
    const { permissions } = useAuthentication();
    const sidebarItems = useSidebarItems();
    const isAuthorized = useMemo(() => {
        const allowedPaths = [
            ...getPathnamesOfItems(
                sidebarItems as unknown as import('@starjack/layout').SidebarEntry[]
            ),
            ...B2B_ALLOWED_EXTRA_PATHS,
        ];
        if (allowedPaths.includes(pathname)) {
            return true;
        }
        return comparePermissions(
            permissions as Permission[],
            permissionsRequired
        );
    }, [permissions, permissionsRequired, sidebarItems, pathname]);

    const checkPermissions = useCallback(
        (permissionsNeeded: Permission[]) => {
            return comparePermissions(
                permissions as Permission[],
                permissionsNeeded
            );
        },
        [permissions]
    );

    return [
        isAuthorized,
        {
            checkPermissions,
        },
    ];
};
